import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { RecordPageShell } from '~/components/layout';
import PlacementIdentityFields from '~/components/pages/ai/placements/PlacementIdentityFields';
import { Alert, Card, CardBody, CardFooter, CardHeader, FormActions } from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { errorText } from '~/lib/apiError';
import {
  DEFAULT_MODEL,
  PLACEMENT_COMPONENTS,
  buildDefaultPlacementDraftState,
  buildInitialCreatePlacementPromptTemplate,
} from '~/lib/ai/placementDefaults';
import {
  normalizePlacementKey,
  placementCreateSchema,
  readPlacementFieldErrors,
  validatePlacementDraftInput,
} from '~/lib/ai/placementValidation';
import { useCreatePlacementMutation } from '~/modules/query/ai/placements';
import { routes } from '~/app/routes';

const FORM_ID = 'ai-placement-create-form';
const INITIAL_CHANGE_NOTE = 'Initial draft created';

interface CreateDraft {
  name: string;
  placementKey: string;
}

type ServerErrors = Partial<Record<keyof CreateDraft, string>>;

/** Create a placement: its name and key. The rest of the draft comes from the component catalogue. */
export default function PlacementCreatePage() {
  const navigate = useNavigate();
  const [createPlacement] = useCreatePlacementMutation();
  const { draft, patch, dirty } = useRecordDraft<null, CreateDraft>(null, {
    id: 'new',
    toDraft: () => ({ name: '', placementKey: '' }),
  });
  const guard = useUnsavedGuard(dirty);
  const check = useFormValidation(placementCreateSchema, draft, (field) =>
    field === 'name' ? 'ai-placement-name' : 'ai-placement-key',
  );
  const [serverErrors, setServerErrors] = useState<ServerErrors>({});

  const save = useRecordSave<CreateDraft>({
    save: async ({ name, placementKey }) => {
      const key = normalizePlacementKey(placementKey);
      const defaults = buildDefaultPlacementDraftState(PLACEMENT_COMPONENTS);
      const validation = validatePlacementDraftInput({
        placementKey: key,
        componentId: defaults.componentId,
        componentVersion: defaults.componentVersion,
        promptTemplate: buildInitialCreatePlacementPromptTemplate(name.trim(), key),
        model: DEFAULT_MODEL,
        name: name.trim(),
        changeNote: INITIAL_CHANGE_NOTE,
        status: 'draft',
      });
      if (!validation.success) throw new Error(validation.error.message);

      try {
        const response = await createPlacement(validation.data).unwrap();
        guard.release();
        navigate(routes.aiPlacement(response.placement.placementKey), { replace: true });
      } catch (error) {
        const fields = readPlacementFieldErrors((error as { data?: unknown } | undefined)?.data);
        const message = errorText(error, '');
        const keyTaken = !fields.placementKey && /already exists|placement key/i.test(message);
        setServerErrors({
          name: fields.name,
          placementKey: fields.placementKey ?? (keyTaken ? message : undefined),
        });
        throw error;
      }
    },
    success: 'Placement created',
    failure: 'The placement could not be created. Try again.',
  });

  return (
    <RecordPageShell
      title="Create placement"
      breadcrumbs={[
        { label: 'AI' },
        { label: 'Placements', href: routes.aiPlacements },
        { label: 'Create placement' },
      ]}
      guard={guard}
      testId="ai-placement-new"
    >
      {draft && (
        <form
          id={FORM_ID}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (!check.submit()) return;
            setServerErrors({});
            save.submit(draft);
          }}
        >
          <Card>
            <CardHeader
              title="Details"
              description="Name it for the team and give it the key your app renders it with. Every other setting is edited on its own page."
            />
            <CardBody className="space-y-6">
              {save.error && <Alert variant="error" message={save.error} />}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <PlacementIdentityFields
                  isNew
                  name={draft.name}
                  placementKey={draft.placementKey}
                  onNameChange={(name) => {
                    patch({ name });
                    setServerErrors((current) => ({ ...current, name: undefined }));
                  }}
                  onNameBlur={() => check.touch('name')}
                  nameError={serverErrors.name ?? check.errors.name}
                  onKeyChange={(placementKey) => {
                    patch({ placementKey });
                    setServerErrors((current) => ({ ...current, placementKey: undefined }));
                  }}
                  onKeyBlur={() => check.touch('placementKey')}
                  keyError={serverErrors.placementKey ?? check.errors.placementKey}
                />
              </div>
            </CardBody>
            <CardFooter>
              <FormActions
                form={FORM_ID}
                discardHref={routes.aiPlacements}
                discardLabel="Cancel"
                saveLabel="Create placement"
                busyLabel="Creating…"
                busy={save.busy}
                saveTestId="ai-placement-create-submit"
              />
            </CardFooter>
          </Card>
        </form>
      )}
    </RecordPageShell>
  );
}
